import { useState } from "react";
import { Loader2, Save, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";

const FIELDS = [
  ["kpi_name", "KPI watched", "text", "e.g. Specific fuel consumption"],
  ["kpi_unit", "Unit of measure", "text", "e.g. kg/thm"],
  ["target_kpi", "Target KPI", "number", "what was promised"],
  ["baseline_kpi", "Baseline KPI", "number", "where it stood before"],
  ["target_impact_lakh", "Target Impact (₹ Lakh)", "number", "in lakh, not crore"],
];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/**
 * The three figures a unit settles once, plus the KPI they are measured on.
 *
 * They are kept on the usecase's compliance profile, which is the only place they live — the
 * odometer, the trend popup and the Data Inconsistency screen all read these same cells.
 */
export const OneTimeForm = ({ card, onSaved, canEdit = true, testId }) => {
  const [form, setForm] = useState({
    kpi_name: card.kpi.name || "",
    kpi_unit: card.kpi.unit || "",
    target_kpi: card.kpi.target ?? "",
    baseline_kpi: card.kpi.baseline ?? "",
    target_impact_lakh: card.impact.target_lakh ?? "",
    family: card.family || "",
    note: card.note || "",
  });
  const [busy, setBusy] = useState(false);
  const gaps = new Set((card.gaps || []).map((g) => g.field));

  const save = async () => {
    if (!form.kpi_name.trim()) {
      toast.error("Name the KPI this usecase is measured on.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/compliance/profiles/${card.project_id}`, {
        kpi_name: form.kpi_name,
        kpi_unit: form.kpi_unit,
        target_kpi: num(form.target_kpi),
        baseline_kpi: num(form.baseline_kpi),
        target_impact_lakh: num(form.target_impact_lakh),
        family: form.family,
        note: form.note,
      });
      toast.success(
        data.changes?.length
          ? `Saved — ${data.changes.map((c) => c.label).join(", ")}.`
          : "Nothing had changed.",
      );
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="label-caps">Settled once · the scale the odometer reads against</div>
        {gaps.size > 0 && (
          <span
            className="flex items-center gap-1.5 text-[11px] font-semibold text-[hsl(var(--watch))]"
            data-testid={`${testId}-gaps`}
          >
            <TriangleAlert className="h-3 w-3" />
            {card.gaps.map((g) => g.label).join(" · ")} still to be declared
          </span>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {FIELDS.map(([key, label, type, hint]) => (
          <label key={key} className="block">
            <span
              className="label-caps block"
              style={gaps.has(key) ? { color: "hsl(var(--watch))" } : undefined}
            >
              {label}
              {gaps.has(key) ? " *" : ""}
            </span>
            <Input
              type={type}
              step="any"
              disabled={!canEdit}
              data-testid={`${testId}-${key}`}
              value={form[key]}
              placeholder={hint}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              className={`mt-1 h-10 rounded-none ${
                gaps.has(key) ? "border-[hsl(var(--watch))]" : ""
              }`}
            />
          </label>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="min-w-[220px]">
          <span className="label-caps block">Family (optional)</span>
          <Input
            disabled={!canEdit}
            data-testid={`${testId}-family`}
            value={form.family}
            placeholder="e.g. Ferro alloy"
            onChange={(e) => setForm((f) => ({ ...f, family: e.target.value }))}
            className="mt-1 h-10 rounded-none"
          />
        </label>
        <label className="min-w-[260px] flex-1">
          <span className="label-caps block">Note (optional)</span>
          <Input
            disabled={!canEdit}
            data-testid={`${testId}-note`}
            value={form.note}
            placeholder="Anything a reviewer should know about how this is measured"
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            className="mt-1 h-10 rounded-none"
          />
        </label>
        {canEdit && (
          <button
            type="button"
            data-testid={`${testId}-save`}
            disabled={busy}
            onClick={save}
            className="flex h-10 items-center gap-2 bg-primary px-4 font-display text-[10px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save these
          </button>
        )}
      </div>
      <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
        Impact is in <b>lakh</b> throughout this screen and on the odometer — a day's impact is far
        too small to read in crore. One crore is a hundred lakh. <b>Family</b> is what to call this
        work when the same thing is being run at other units — leave it blank and the portal groups
        it itself, from the KPI and the title.
      </p>
    </div>
  );
};
